/* ═══════════════════════════════════════════════════════
   SwipeHome — Router Module
   Navigation, render engine, tab bar, screen registry
   ═══════════════════════════════════════════════════════ */

import { state } from './state.js';
import type { Screen, ScreenRegistry, ScreenRenderer } from './types/ui.js';

// Screen registry — populated by registerScreens()
let _screens: ScreenRegistry = {};
let _renderTabBarFn: ((tabBar: HTMLElement) => void) | null = null;

export function registerScreens(map: ScreenRegistry): void {
  _screens = map;
}

export function setTabBarRenderer(fn: (tabBar: HTMLElement) => void): void {
  _renderTabBarFn = fn;
}

// ─── Navigation ────────────────────────────────────────
// Root tab screens — navigating to these resets the stack
export const ROOT_TABS: ReadonlySet<Screen> = new Set<Screen>([
  'feed',
  'likes',
  'portfolio',
  'profile',
  'providers',
]);

// Scroll positions keyed by screen name
const _scrollPositions: Partial<Record<Screen, number>> = {};

export function navigate(screen: Screen, data?: Record<string, unknown>): void {
  // Save scroll position of current screen
  _scrollPositions[state.currentScreen] = window.scrollY || document.documentElement.scrollTop;

  if (ROOT_TABS.has(screen)) {
    // Navigating to a root tab — reset the stack
    state.screenStack = [];
  } else if (state.currentScreen !== screen) {
    // Navigating into a sub-view — push current screen onto stack
    state.screenStack.push(state.currentScreen);
  }

  state.currentScreen = screen;
  if (data) Object.assign(state, data);
  render();

  // Push browser history for sub-views so Android back button works
  if (!ROOT_TABS.has(screen)) {
    history.pushState({ screen, stackDepth: state.screenStack.length }, '', '');
  } else {
    history.replaceState({ screen, stackDepth: 0 }, '', '');
  }
}

export function goBack(): boolean {
  const prev = state.screenStack.pop();
  if (prev) {
    // Save scroll position of current screen
    _scrollPositions[state.currentScreen] = window.scrollY || document.documentElement.scrollTop;

    // If returning to feed, skip re-fetch and restore existing state
    if (prev === 'feed' && state.feed.length > 0) {
      state.feedRestoring = true;
    }
    state.currentScreen = prev;
    state._isGoingBack = true;
    render();

    // Restore scroll position
    const savedScroll = _scrollPositions[prev];
    if (savedScroll !== undefined) {
      requestAnimationFrame(() => window.scrollTo(0, savedScroll));
    }

    state._isGoingBack = false;
    return true;
  }
  return false;
}

// Android hardware back button / browser back button
window.addEventListener('popstate', () => {
  if (state.screenStack.length > 0) {
    goBack();
  }
  // If stack is empty, we're at a root tab — let the browser handle it naturally
});

// Set initial history state
if (typeof history.replaceState === 'function') {
  history.replaceState({ screen: state.currentScreen, stackDepth: 0 }, '', '');
}

// ─── Render Engine ─────────────────────────────────────
export function render(): void {
  const container = document.getElementById('screen-container');
  const tabBar = document.getElementById('tab-bar');
  if (!container || !tabBar) return;

  // Clean up any body-level transient overlays that outlive the screen
  // they were spawned on (swipe tutorial, etc.) so they don't leak
  // across navigation.
  document.querySelectorAll('#swipe-tutorial-overlay, .transient-overlay').forEach((el) => el.remove());

  const renderFn: ScreenRenderer | undefined = _screens[state.currentScreen];
  if (renderFn) {
    container.innerHTML = '';
    const el = renderFn();
    if (el) {
      if (typeof el === 'string') {
        container.innerHTML = el;
      } else {
        container.appendChild(el);
      }
      // Apply directional transition: slide-in-left for back, slide-in-right
      // for sub-views, fade-in for root tabs
      const target = typeof el === 'string' ? (container.firstElementChild as HTMLElement | null) : el;
      if (target) {
        const isRootTab = ROOT_TABS.has(state.currentScreen);
        if (state._isGoingBack) {
          target.className = target.className.replace(/\bfade-in\b/, '').trim() + ' slide-in-left';
        } else if (!isRootTab) {
          target.className = target.className.replace(/\bfade-in\b/, '').trim() + ' slide-in-right';
        }
      }
    }
    if (!state._isGoingBack) {
      container.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  }

  // Tab bar
  const authScreens: Screen[] = [
    'gateKey',
    'login',
    'signup',
    'roleSelect',
    'preferences',
    'onboarding',
  ];
  if (authScreens.includes(state.currentScreen)) {
    tabBar.classList.add('hidden');
  } else {
    tabBar.classList.remove('hidden');
    if (_renderTabBarFn) _renderTabBarFn(tabBar);
    else renderTabBar(tabBar);
  }

  // Feather icons
  if (window.feather) window.feather.replace();
}

// ─── Tab Bar ───────────────────────────────────────────
interface TabDefinition {
  id: Screen;
  icon: string;
  label: string;
}

export function renderTabBar(tabBar: HTMLElement): void {
  const role = state.role;
  let tabs: TabDefinition[] = [];

  if (role === 'buyer') {
    // Investor-first convergence: ONE tab bar for all buyer-role users.
    // Providers moved into Portfolio as a sub-section.
    tabs = [
      { id: 'feed', icon: 'compass', label: 'Discover' },
      { id: 'likes', icon: 'heart', label: 'Watchlist' },
      { id: 'portfolio', icon: 'briefcase', label: 'Portfolio' },
      { id: 'profile', icon: 'user', label: 'Profile' },
    ];
  }

  // Determine which tab is "active" — if we're on a sub-view, highlight the
  // root tab we came from
  const activeTab: Screen = ROOT_TABS.has(state.currentScreen)
    ? state.currentScreen
    : (state.screenStack.find((s) => ROOT_TABS.has(s)) as Screen | undefined) || state.currentScreen;

  tabBar.innerHTML = tabs
    .map(
      (t) => `
    <button class="tab-item ${activeTab === t.id ? 'active' : ''}" onclick="navigate('${t.id}')" style="position:relative">
      <i data-feather="${t.icon}"></i>
      <span>${t.label}</span>
      ${t.id === ('notifications' as Screen) && state.unreadCount > 0 ? `<span class="tab-badge">${state.unreadCount > 9 ? '9+' : state.unreadCount}</span>` : ''}
    </button>
  `,
    )
    .join('');
}
